/** Agent and expert verdicts on a task in quality check, shown beside its stage. */
import { Icon, type IconName } from './Icon'
import type { QcSignals as Signals } from '@/lib/types'
import { useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type State = 'waiting' | 'running' | 'pass' | 'fail' | 'error'

const plural = (n: number) => `${n} expert${n === 1 ? '' : 's'}`

function agent(verdict: Signals['agent']): [State, string] {
  switch (verdict) {
    case 'ready': return ['pass', 'Agent review: ready']
    case 'needs_changes': return ['fail', 'Agent review: needs changes']
    case 'running': return ['running', 'Agent review: in progress']
    case 'unavailable': return ['error', 'Agent review: status unavailable']
    case 'failed': return ['error', 'Agent review: did not complete']
    default: return ['waiting', 'Agent review: not started']
  }
}

function expert({ verdict, ready, needs_changes }: Signals['expert']): [State, string] {
  const total = ready + needs_changes
  if (verdict === 'needs_changes') return ['fail', `Expert review: needs changes (${needs_changes} of ${plural(total)})`]
  if (verdict === 'ready') return ['pass', `Expert review: ready (${plural(ready)})`]
  return ['waiting', 'Expert review: no recommendation yet']
}

const MARK: Record<State, IconName> = { waiting: 'minus', running: 'clock', pass: 'check', fail: 'close', error: 'alert' }

function Signal({ who, state }: { who: IconName; state: State }) {
  return (
    <span className={`qc-signal ${state}`} aria-hidden="true">
      <Icon name={who} size={14} />
      <Icon name={MARK[state]} size={10} />
    </span>
  )
}

export function QcSignals({ signals, children }: { signals: Signals; children: ReactNode }) {
  const id = useId()
  const anchor = useRef<HTMLSpanElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const show = () => {
    const rect = anchor.current?.getBoundingClientRect()
    if (rect) setPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 288)),
      top: rect.bottom + 88 < window.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - 82),
    })
  }
  const [agentState, agentLabel] = agent(signals.agent)
  const [expertState, expertLabel] = expert(signals.expert)
  return (
    <span className="qc-stage" ref={anchor} tabIndex={0} aria-describedby={position ? id : undefined}
      onMouseEnter={show} onMouseLeave={() => setPosition(null)} onFocus={show} onBlur={() => setPosition(null)}
      onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); setPosition(null) } }}>
      {children}
      {position && createPortal(<span className="qc-tooltip" id={id} role="tooltip" style={position}>
        <span><Signal who="agent" state={agentState} />{agentLabel}</span>
        <span><Signal who="user" state={expertState} />{expertLabel}</span>
      </span>, document.body)}
    </span>
  )
}
